import { Form, Modal } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DepartmentTreeSelect } from "@/shared/components/DepartmentTreeSelect";
import type { Department } from "../types";

const ROOT_VALUE = "__root__";

/**
 * `DepartmentTreeSelect` faqat `Department[]` qabul qiladi — sintetik
 * "Ildiz (ota bo'limsiz)" tanlovini ko'rsatish uchun uni oddiy qator
 * sifatida ro'yxatga qo'shamiz (`path` daraxtda ishlatilmaydi, faqat
 * ko'chirish tekshiruvi uchun kerak — bu yerda ahamiyatsiz).
 */
function rootOption(rootLabel: string): Department {
	return {
		id: ROOT_VALUE,
		parentId: null,
		name: rootLabel,
		path: "/",
		gradeId: "",
		gradeLevel: 0,
		gradeName: "",
		code: null,
		isActive: true,
		archivedAt: null,
		createdAt: new Date().toISOString(),
	};
}

interface MoveDepartmentModalProps {
	open: boolean;
	department: Department | null;
	departments: Department[];
	isSubmitting: boolean;
	onCancel: () => void;
	onSubmit: (parentId: string | null) => void;
}

/**
 * Drag'siz ko'chirish: tugun tanlanadi → bu modalda yangi ota-ona tanlanadi →
 * `PATCH { parentId }`. O'zini va avlodlarini ota-ona sifatida tanlash
 * ro'yxatdan chiqarib tashlanadi (`path` prefiks — `buildPath` backend'da
 * o'z ID'sini ham qo'shadi, shu sabab avlodlar `path.startsWith(current.path)`
 * bilan aniqlanadi). Aylanma/chuqurlik chegarasini backend baribir 422 bilan
 * qaytadan tekshiradi — bu yerdagi filtr faqat operatorga qulaylik.
 */
export function MoveDepartmentModal({
	open,
	department,
	departments,
	isSubmitting,
	onCancel,
	onSubmit,
}: MoveDepartmentModalProps) {
	const { t } = useTranslation("departments");
	const [form] = Form.useForm<{ parentId: string }>();

	useEffect(() => {
		if (open) {
			form.resetFields();
			form.setFieldsValue({ parentId: department?.parentId ?? ROOT_VALUE });
		}
	}, [open, department, form]);

	const availableDepartments = department
		? [
				rootOption(t("move.rootOption")),
				...departments.filter((candidate) => !candidate.path.startsWith(department.path)),
			]
		: [];

	return (
		<Modal
			open={open}
			title={department ? t("move.titleWithName", { name: department.name }) : t("move.title")}
			okText={t("move.okText")}
			cancelText={t("move.cancelText")}
			confirmLoading={isSubmitting}
			onCancel={onCancel}
			onOk={() => form.submit()}
			destroyOnHidden
		>
			<Form
				form={form}
				layout="vertical"
				onFinish={(values) => onSubmit(values.parentId === ROOT_VALUE ? null : values.parentId)}
			>
				<Form.Item
					name="parentId"
					label={t("move.newParentLabel")}
					rules={[{ required: true, message: t("move.newParentRequired") }]}
				>
					<DepartmentTreeSelect departments={availableDepartments} />
				</Form.Item>
			</Form>
		</Modal>
	);
}
